'use client'

import {
    createStoreThemeDemo,
    listStoreThemeStores,
    listStoreThemes,
    type StoreThemeStore,
    type StoreThemeSummary,
} from '@/lib/api/commerce/store-themes'
import { THEME_STYLE_TAGS } from '@mediardex/contracts'
import agendaPreview from '@/assets/theme-previews/agenda.webp'
import alvaPreview from '@/assets/theme-previews/alva.webp'
import arenaPreview from '@/assets/theme-previews/arena.webp'
import atelierPreview from '@/assets/theme-previews/atelier.webp'
import aureaPreview from '@/assets/theme-previews/aurea.webp'
import bistroPreview from '@/assets/theme-previews/bistro.webp'
import carePreview from '@/assets/theme-previews/care.webp'
import circuitPreview from '@/assets/theme-previews/circuit.webp'
import classicPreview from '@/assets/theme-previews/classic.webp'
import essencePreview from '@/assets/theme-previews/essence.webp'
import foundationPreview from '@/assets/theme-previews/foundation.webp'
import glowPreview from '@/assets/theme-previews/glow.webp'
import harvestPreview from '@/assets/theme-previews/harvest.webp'
import homestorePreview from '@/assets/theme-previews/homestore.webp'
import linenPreview from '@/assets/theme-previews/linen.webp'
import lumenPreview from '@/assets/theme-previews/lumen.webp'
import maisonPreview from '@/assets/theme-previews/maison.webp'
import marketPreview from '@/assets/theme-previews/market.webp'
import mascotPreview from '@/assets/theme-previews/mascot.webp'
import neonPreview from '@/assets/theme-previews/neon.webp'
import noirPreview from '@/assets/theme-previews/noir.webp'
import nordicPreview from '@/assets/theme-previews/nordic.webp'
import pawPreview from '@/assets/theme-previews/paw.webp'
import pilotPreview from '@/assets/theme-previews/pilot.webp'
import pixelPreview from '@/assets/theme-previews/pixel.webp'
import podiumPreview from '@/assets/theme-previews/podium.webp'
import routePreview from '@/assets/theme-previews/route.webp'
import runwayPreview from '@/assets/theme-previews/runway.webp'
import snoutPreview from '@/assets/theme-previews/snout.webp'
import sparklePreview from '@/assets/theme-previews/sparkle.webp'
import trailPreview from '@/assets/theme-previews/trail.webp'
import turboPreview from '@/assets/theme-previews/turbo.webp'
import { useEffect, useMemo, useState } from 'react'
import {
    MdBrush,
    MdCheckCircle,
    MdClose,
    MdDesktopWindows,
    MdOpenInNew,
    MdPalette,
    MdPhoneIphone,
    MdStorefront,
    MdVisibility,
} from 'react-icons/md'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'

/**
 * Capa de cada tema: screenshot da DEMONSTRAÇÃO (computador + celular), capturado da
 * própria demo (docs/store-themes.md §14.13). Tema novo: capturar a capa na demo e
 * registrar aqui; sem capa, o card cai na amostra de cores.
 */
const THEME_PREVIEW_IMAGES: Record<string, string> = {
    classic: classicPreview,
    maison: maisonPreview,
    linen: linenPreview,
    runway: runwayPreview,
    noir: noirPreview,
    atelier: atelierPreview,
    nordic: nordicPreview,
    homestore: homestorePreview,
    alva: alvaPreview,
    essence: essencePreview,
    glow: glowPreview,
    lumen: lumenPreview,
    aurea: aureaPreview,
    sparkle: sparklePreview,
    circuit: circuitPreview,
    neon: neonPreview,
    pixel: pixelPreview,
    arena: arenaPreview,
    podium: podiumPreview,
    trail: trailPreview,
    route: routePreview,
    turbo: turboPreview,
    pilot: pilotPreview,
    market: marketPreview,
    bistro: bistroPreview,
    harvest: harvestPreview,
    mascot: mascotPreview,
    snout: snoutPreview,
    paw: pawPreview,
    agenda: agendaPreview,
    foundation: foundationPreview,
    care: carePreview,
}

function formatDate(value: string): string {
    return new Date(value).toLocaleDateString('pt-BR')
}

/**
 * "Temas da Loja" é a LOJA DE TEMAS (decisão do owner em 2026-10-18): a galeria em
 * página cheia, com o tema ativo da loja marcado e o Personalizar nele. A listagem de
 * lojas não mora aqui — ela já existe em "Minhas Lojas"; com mais de uma loja, um
 * seletor no topo diz de qual loja se está falando (`?store=` guarda a escolha).
 */
export default function StoreThemesPage() {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const [stores, setStores] = useState<StoreThemeStore[]>([])
    const [themes, setThemes] = useState<StoreThemeSummary[]>([])
    const [loading, setLoading] = useState(true)
    const [tag, setTag] = useState('')
    const [demoBusy, setDemoBusy] = useState('')
    // Demonstração num modal de tela quase cheia, como na Nuvemshop: o lojista olha o tema
    // sem sair da galeria. "Outra aba" continua lá para quem quiser.
    const [demo, setDemo] = useState<{ slug: string; name: string; url: string; catalog: string } | null>(null)
    const [demoDevice, setDemoDevice] = useState<'desktop' | 'mobile'>('desktop')

    useEffect(() => {
        Promise.all([listStoreThemeStores(), listStoreThemes()])
            .then(([storesRes, themesRes]) => {
                if (storesRes.success) setStores(storesRes.result ?? [])
                else toast.error(storesRes.error || 'Não foi possível carregar as lojas')
                if (themesRes.success) setThemes(themesRes.result ?? [])
                else toast.error(themesRes.error || 'Não foi possível carregar os temas')
            })
            .finally(() => setLoading(false))
    }, [])

    const storeParam = Number(searchParams.get('store'))
    const store = useMemo(
        () => stores.find((item) => item.id === storeParam) ?? stores[0] ?? null,
        [stores, storeParam],
    )

    const usedTags = THEME_STYLE_TAGS.filter((option) => themes.some((theme) => theme.styleTags.includes(option.value)))
    const visibleThemes = themes.filter((theme) => !tag || theme.styleTags.includes(tag))

    // "Ver com segmento de": no topo do modal, à vista. Embaixo, na barra da loja, passava
    // despercebido (os banners e os produtos puxam o olho primeiro).
    const changeDemoCatalog = async (catalog: string) => {
        if (!demo || !store) return
        setDemoBusy(demo.slug)
        try {
            const res = await createStoreThemeDemo(store.id, demo.slug, catalog || undefined)
            if (res.success && res.result) setDemo({ ...demo, url: res.result.url, catalog })
            else toast.error(res.error || 'Não foi possível trocar os produtos da demonstração')
        } finally {
            setDemoBusy('')
        }
    }

    return (
        <div className="mx-auto max-w-6xl space-y-6 p-6">
            <div className="flex flex-wrap items-center gap-3">
                <MdPalette className="text-3xl text-secondary" />
                <div className="min-w-0 flex-1">
                    <h1 className="text-2xl font-bold">Temas da Loja</h1>
                    <p className="text-sm text-base-content/60">
                        Escolha um tema pronto e personalize cores, fontes e as seções de cada página, com a prévia
                        ao lado. A loja só muda quando você publica.
                    </p>
                </div>
                {stores.length > 1 && store && (
                    <label className="flex items-center gap-2 text-sm">
                        <span className="text-base-content/60">Loja</span>
                        <select
                            className="select select-bordered select-sm"
                            value={store.id}
                            onChange={(e) => setSearchParams({ store: e.target.value })}
                        >
                            {stores.map((item) => (
                                <option key={item.id} value={item.id}>
                                    {item.name}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
            </div>

            {loading ? (
                <div className="flex h-40 items-center justify-center">
                    <span className="loading loading-spinner loading-lg" />
                </div>
            ) : !store ? (
                <div className="card bg-base-100 shadow-sm">
                    <div className="card-body items-center gap-3 text-center">
                        <MdStorefront className="text-5xl text-base-content/30" />
                        <p className="text-base-content/70">Esta empresa ainda não tem loja online.</p>
                        <Link to="/commerce/stores/new" className="btn btn-primary btn-sm">
                            Criar loja
                        </Link>
                    </div>
                </div>
            ) : (
                <>
                    {usedTags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            <button
                                type="button"
                                className={`btn btn-xs ${tag === '' ? 'btn-primary' : 'btn-ghost'}`}
                                onClick={() => setTag('')}
                            >
                                Todos
                            </button>
                            {usedTags.map((option) => (
                                <button
                                    type="button"
                                    key={option.value}
                                    className={`btn btn-xs ${tag === option.value ? 'btn-primary' : 'btn-ghost'}`}
                                    onClick={() => setTag(tag === option.value ? '' : option.value)}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {visibleThemes.map((theme) => {
                            const active = theme.slug === store.themeSlug
                            return (
                                <div
                                    key={theme.slug}
                                    className={`card overflow-hidden bg-base-100 shadow-sm ${active ? 'ring-2 ring-primary' : ''}`}
                                >
                                    {/* Capa: a demonstração do tema (computador + celular). Sem capa, a amostra de cores. */}
                                    {THEME_PREVIEW_IMAGES[theme.slug] ? (
                                        <img
                                            src={THEME_PREVIEW_IMAGES[theme.slug]}
                                            alt={`Prévia do tema ${theme.name}`}
                                            // Com dezenas de temas, só as capas à vista são baixadas.
                                            loading="lazy"
                                            decoding="async"
                                            width={720}
                                            height={540}
                                            className="aspect-[4/3] w-full border-b border-base-200 object-cover object-top"
                                        />
                                    ) : (
                                        <div
                                            className="flex h-28 flex-col justify-center gap-1.5 border-b border-base-200 px-5"
                                            style={{ backgroundColor: theme.sample.colorBackground }}
                                        >
                                            <span
                                                className="truncate text-2xl leading-tight"
                                                style={{ fontFamily: `'${theme.sample.headingFont}', serif`, color: theme.sample.colorPrimary }}
                                            >
                                                {theme.name}
                                            </span>
                                            <span className="flex items-center gap-1.5">
                                                {[theme.sample.colorPrimary, theme.sample.colorSecondary, theme.sample.colorAccent].map(
                                                    (color, index) => (
                                                        <span
                                                            key={index}
                                                            className="h-4 w-4 rounded-full border border-black/10"
                                                            style={{ backgroundColor: color }}
                                                        />
                                                    ),
                                                )}
                                            </span>
                                        </div>
                                    )}
                                    <div className="card-body gap-3">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <h2 className="font-semibold">{theme.name}</h2>
                                            {active && (
                                                <span className="badge badge-success badge-sm gap-1">
                                                    <MdCheckCircle /> Ativo
                                                </span>
                                            )}
                                            {active && store.hasDraft && (
                                                <span className="badge badge-warning badge-sm">Rascunho não publicado</span>
                                            )}
                                        </div>
                                        <p className="text-sm text-base-content/70">{theme.description}</p>
                                        {/* Sem a etiqueta de segmento no card (pedido do owner, 2026-10-22):
                                            ela fazia o tema parecer exclusivo de um ramo. O filtro acima
                                            continua ajudando a achar, e a demonstração mostra qualquer
                                            segmento ("Ver com segmento de"). */}
                                        {active && (
                                            <span className="text-xs text-base-content/60">
                                                {store.publishedAt
                                                    ? `Publicado em ${formatDate(store.publishedAt)}`
                                                    : 'Ainda não personalizado: usa a configuração original da loja.'}
                                            </span>
                                        )}
                                        <div className="card-actions justify-end">
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-sm"
                                                disabled={demoBusy === theme.slug}
                                                onClick={async () => {
                                                    setDemoBusy(theme.slug)
                                                    try {
                                                        const res = await createStoreThemeDemo(store.id, theme.slug)
                                                        if (res.success && res.result) {
                                                            setDemoDevice('desktop')
                                                            setDemo({
                                                                slug: theme.slug,
                                                                name: theme.name,
                                                                url: res.result.url,
                                                                catalog: theme.styleTags[0] ?? '',
                                                            })
                                                        } else toast.error(res.error || 'Não foi possível abrir a demonstração')
                                                    } finally {
                                                        setDemoBusy('')
                                                    }
                                                }}
                                            >
                                                <MdVisibility /> Ver demonstração
                                            </button>
                                            {active ? (
                                                <button
                                                    type="button"
                                                    className="btn btn-primary btn-sm"
                                                    onClick={() => navigate(`/commerce/themes/${store.id}`)}
                                                >
                                                    <MdBrush /> Personalizar
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="btn btn-outline btn-sm"
                                                    onClick={() => navigate(`/commerce/themes/${store.id}?theme=${theme.slug}`)}
                                                >
                                                    Usar este tema
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </>
            )}

            {demo && store && (
                <dialog className="modal modal-open z-50">
                    <div className="modal-box flex h-[94vh] w-[97vw] !max-w-none flex-col gap-3 !p-3">
                        <div className="flex flex-wrap items-center gap-2 px-1">
                            <span className="font-semibold">Demonstração: {demo.name}</span>
                            <label className="flex items-center gap-2 text-sm">
                                <span className="whitespace-nowrap text-base-content/70">Ver com segmento de</span>
                                <select
                                    className="select select-bordered select-sm w-auto"
                                    value={demo.catalog}
                                    disabled={demoBusy === demo.slug}
                                    onChange={(event) => changeDemoCatalog(event.target.value)}
                                >
                                    {!demo.catalog && <option value="">Todos os segmentos</option>}
                                    {THEME_STYLE_TAGS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                {demoBusy === demo.slug && <span className="loading loading-spinner loading-xs" />}
                            </label>
                            <div className="ml-auto flex items-center gap-2">
                                <div className="join">
                                    <button
                                        type="button"
                                        className={`btn btn-sm join-item ${demoDevice === 'desktop' ? 'btn-active' : ''}`}
                                        onClick={() => setDemoDevice('desktop')}
                                        title="Computador"
                                    >
                                        <MdDesktopWindows />
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn btn-sm join-item ${demoDevice === 'mobile' ? 'btn-active' : ''}`}
                                        onClick={() => setDemoDevice('mobile')}
                                        title="Celular"
                                    >
                                        <MdPhoneIphone />
                                    </button>
                                </div>
                                <a href={demo.url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-ghost">
                                    <MdOpenInNew /> Outra aba
                                </a>
                                {demo.slug === store.themeSlug ? (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-primary"
                                        onClick={() => navigate(`/commerce/themes/${store.id}`)}
                                    >
                                        <MdBrush /> Personalizar
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-primary"
                                        onClick={() =>
                                            navigate(
                                                `/commerce/themes/${store.id}?theme=${demo.slug}` +
                                                    (demo.catalog ? `&segment=${encodeURIComponent(demo.catalog)}` : ''),
                                            )
                                        }
                                    >
                                        Usar este tema
                                    </button>
                                )}
                                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setDemo(null)} title="Fechar">
                                    <MdClose className="text-lg" />
                                </button>
                            </div>
                        </div>
                        <div className="flex min-h-0 flex-1 justify-center overflow-auto rounded-lg bg-base-200 p-2">
                            <iframe
                                src={demo.url}
                                title={`Demonstração do tema ${demo.name}`}
                                className={`h-full rounded-lg border border-base-300 bg-white shadow-sm transition-[width] ${demoDevice === 'mobile' ? 'w-[390px]' : 'w-full'}`}
                            />
                        </div>
                    </div>
                    <div className="modal-backdrop" onClick={() => setDemo(null)} />
                </dialog>
            )}
        </div>
    )
}
